import { formatDistanceToNow } from 'date-fns';
import {
  PulseConfidenceTier,
  PulseDirection,
  PulseGeographyScope,
  PulseListItem,
  PulseSnapshotSummary,
  PulseStatus,
  PulseVertical,
  PULSE_GEOGRAPHY_OPTIONS,
  PULSE_VERTICAL_OPTIONS,
} from './pulse.types';

export function pulseSurveyPath(tab: string, clientId?: string, search?: string): string {
  const base = clientId ? `/client/${clientId}/surveys/${tab}` : `/surveys/${tab}`;
  return search ? `${base}?${search}` : base;
}

export function formatVertical(vertical: PulseVertical): string {
  return PULSE_VERTICAL_OPTIONS.find((opt) => opt.value === vertical)?.label ?? vertical;
}

export function formatGeography(scope: PulseGeographyScope): string {
  return PULSE_GEOGRAPHY_OPTIONS.find((opt) => opt.value === scope)?.label ?? scope;
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '—';
  }
}

export function formatConfidence(tier: PulseConfidenceTier | undefined): string {
  switch (tier) {
    case 'well_measured':
      return 'Well measured';
    case 'partially_measured':
      return 'Partial';
    case 'thinly_measured':
      return 'Thin';
    default:
      return '—';
  }
}

export function formatPositionValue(snapshot: PulseSnapshotSummary | null | undefined): string {
  if (!snapshot) return '—';
  const raw = Number(snapshot.value);
  if (Number.isNaN(raw)) return String(snapshot.value);
  // Position is stored as a fraction 0–1 in snapshots; display as percent when in range.
  if (raw >= 0 && raw <= 1) {
    return `${Math.round(raw * 100)}%`;
  }
  return String(Math.round(raw));
}

export function formatDirectionDelta(
  direction: PulseDirection | undefined,
  snapshot: PulseSnapshotSummary | null | undefined,
): { label: string; tone: 'up' | 'down' | 'flat' | 'none' } {
  if (!snapshot || !direction) {
    return { label: '', tone: 'none' };
  }
  const provenance = snapshot.provenance || {};
  const deltaRaw = provenance.delta ?? provenance.change ?? provenance.point_change;
  if (typeof deltaRaw === 'number') {
    const pct = Math.abs(deltaRaw) <= 1 ? Math.round(deltaRaw * 100) : Math.round(deltaRaw);
    if (pct > 0) return { label: `+${pct}`, tone: 'up' };
    if (pct < 0) return { label: `${pct}`, tone: 'down' };
    return { label: '0', tone: 'flat' };
  }
  if (direction === 'rising') return { label: '↑', tone: 'up' };
  if (direction === 'falling') return { label: '↓', tone: 'down' };
  return { label: '→', tone: 'flat' };
}

export function statusBadgeClass(status: PulseStatus): string {
  if (status === 'active') {
    return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
  }
  return 'bg-gray-100 text-gray-600 border border-gray-200';
}

export function confidenceBadgeClass(tier: PulseConfidenceTier | undefined): string {
  switch (tier) {
    case 'well_measured':
      return 'bg-amber-50 text-amber-900 border border-amber-200';
    case 'partially_measured':
      return 'bg-rose-50 text-rose-800 border border-rose-200';
    case 'thinly_measured':
      return 'text-red-600 border border-transparent';
    default:
      return 'bg-gray-50 text-gray-500 border border-gray-200';
  }
}

export function cadenceLabel(item: Pick<PulseListItem, 'cadence' | 'construct_version'>): string {
  const cadence = item.cadence === 'weekly' ? 'Weekly' : 'Daily';
  return `${cadence} · construct v${item.construct_version}`;
}

export function needsLookBanner(provenance: Record<string, unknown> | undefined): string | null {
  if (!provenance || Object.keys(provenance).length === 0) return null;
  const message =
    (typeof provenance.needs_look === 'string' && provenance.needs_look) ||
    (typeof provenance.alert === 'string' && provenance.alert) ||
    (typeof provenance.note === 'string' && provenance.note) ||
    null;
  if (message) return message;
  if (provenance.campaign_cap_bound || provenance.caps_bound) {
    return 'A measurement cap bound this window. Review provenance before publishing.';
  }
  return null;
}
